import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface CloseListingModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: (price: number | null, quantity?: number) => void;
    listingType: 'WTS' | 'WTB';
    maxQuantity?: number;
    defaultPrice?: number;
}

const CloseListingModal: React.FC<CloseListingModalProps> = ({
    isOpen,
    onClose,
    onConfirm,
    listingType,
    maxQuantity = 1,
    defaultPrice
}) => {
    const { t } = useTranslation();
    const [closingPrice, setClosingPrice] = useState<string>(defaultPrice ? defaultPrice.toString() : '');
    const [quantity, setQuantity] = useState<string>('1');

    // Update initial state when modal opens
    React.useEffect(() => {
        if (isOpen) {
            setClosingPrice(defaultPrice ? defaultPrice.toString() : '');
            setQuantity('1');
        }
    }, [isOpen, defaultPrice]);

    if (!isOpen) return null;

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        const price = closingPrice ? parseFloat(closingPrice) : null;
        const qty = parseInt(quantity) || 1;
        onConfirm(price, qty);
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
                <h2 className="text-xl font-bold mb-4">
                    {t('closeListing.title', { type: listingType })}
                </h2>

                <p className="text-gray-600 mb-6">
                    {t('closeListing.description', {
                        action: listingType === 'WTS' ? t('closeListing.sold') : t('closeListing.bought')
                    })}
                </p>

                <form onSubmit={handleSubmit}>
                    {maxQuantity > 1 && (
                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                {t('closeListing.quantityLabel')}
                            </label>
                            <div className="flex items-center gap-2">
                                <input
                                    type="number"
                                    min="1"
                                    max={maxQuantity}
                                    value={quantity}
                                    onChange={(e) => setQuantity(e.target.value)}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                                <span className="text-xs text-gray-500 whitespace-nowrap">
                                    {t('closeListing.quantityHelper', { count: maxQuantity })}
                                </span>
                            </div>
                        </div>
                    )}

                    <div className="mb-6">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            {t('closeListing.finalPriceLabel')}
                        </label>
                        <div className="flex items-center gap-2">
                            <input
                                type="number"
                                min="0"
                                step="0.01"
                                value={closingPrice}
                                onChange={(e) => setClosingPrice(e.target.value)}
                                placeholder="0.00"
                                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                required
                            />
                            <span className="text-gray-500">THB</span>
                        </div>
                    </div>

                    <div className="flex gap-3">
                        <button
                            type="button"
                            onClick={onClose}
                            className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition font-medium"
                        >
                            {t('common.cancel')}
                        </button>
                        <button
                            type="submit"
                            className="flex-1 px-4 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition font-bold"
                        >
                            {t('closeListing.confirmClose')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default CloseListingModal;
